import React from "react"
import { FormattedMessage } from "react-intl"
import { useHistory } from "react-router-dom"
import { Progress, Typography, Link, Stack, useTheme, Box, Alert, Button } from "@carrotfertility/carotene-core"
import { useGetMemberIdInfo } from "#/components/member-id/hooks/useGetMemberIdInfo"
import { MemberIdCard } from "#/components/member-id/MemberIdCard"
import { PartnerProvidersSection } from "#/components/member-id/PartnerProvidersSection"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader } from "#/features/global-ui/page-header"
import { MemberIdError } from "#/pages/member-id/MemberIdError"
import { Paths, TtcPaths, DbgPaths, CarrotPlansPaths } from "#/utils/Paths"
import useGetMemberBenefit from "#/components/carrot-plans/hooks/useGetMemberBenefit"
import useCarrotPlan from "#/components/carrot-plans/hooks/useCarrotPlan"
import { PartnerEligibilityStatus } from "#/utils/PartnerEligibilityStatus"
import { useBenefitEnrollmentStatus } from "#/components/context/enrollment/hooks/useBenefitEnrollmentStatus"
import Locations from "#/components/views/home/navigation"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"
import { useCurrentUser } from "#/components/context/user/UserContext"

export function EnterpriseMemberIdPage(): JSX.Element {
  const {
    firstName,
    lastName,
    isUsa,
    memberId,
    effectiveDate,
    partnerFirstName,
    partnerLastName,
    isError,
    isLoading,
    partnerEligibilityStatus,
    pharmacyInfo
  } = useGetMemberIdInfo()
  const theme = useTheme()
  const { hasCarrotPlan } = useCarrotPlan()
  const { data: benefitEnrollmentStatus } = useBenefitEnrollmentStatus()
  const history = useHistory()
  const { data: cpRequestStatus } = useGetCarrotPlanRequestStatus()
  const isCarrotPlanPending = cpRequestStatus?.carrotPlanIsOnTheWay ?? false
  const {
    company: { carrotLite }
  } = useCurrentUser()

  const partnerShouldAppearOnMemberCard =
    partnerFirstName && partnerEligibilityStatus === PartnerEligibilityStatus.ELIGIBLE

  const partnerNotEligible = partnerFirstName && partnerEligibilityStatus === PartnerEligibilityStatus.NOT_ELIGIBLE

  const { data: memberBenefit } = useGetMemberBenefit()
  const hasProviderFinder = memberBenefit?.providerFinder?.showProviderFinder ?? false
  const showCarrotPlanAlert = !hasCarrotPlan && !isCarrotPlanPending
  const showBenefitEnrollmentAlert =
    !carrotLite && !showCarrotPlanAlert && (benefitEnrollmentStatus?.needsBenefitEnrollment ?? false)

  const accountSettingsUrl = `${Paths.ACCOUNT}#partnerInformationSettings`

  return (
    <PageLayout header={<PageHeader pageTitle={<FormattedMessage defaultMessage="Member ID" />} />}>
      {isError ? (
        <MemberIdError />
      ) : isLoading ? (
        <Progress />
      ) : (
        <>
          {showCarrotPlanAlert && (
            <Box marginBottom={theme.spacing(theme.tokens.spacing.lg)}>
              <Alert severity="warning">
                <Typography>
                  <FormattedMessage defaultMessage="To access savings and other benefits at partner providers, get your Carrot Plan. " />
                </Typography>
                <Box marginTop={theme.spacing(theme.tokens.spacing.sm)}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{
                      backgroundColor: "white"
                    }}
                    onClick={() => {
                      window.location.href = CarrotPlansPaths.ENROLLMENT
                    }}
                  >
                    <FormattedMessage defaultMessage="Get Carrot Plan" />
                  </Button>
                </Box>
              </Alert>
            </Box>
          )}

          {showBenefitEnrollmentAlert && (
            <Box marginBottom={theme.spacing(theme.tokens.spacing.lg)}>
              <Alert severity="warning">
                <Typography>
                  <FormattedMessage defaultMessage="To access your funds, share some information about your health insurance coverage. These details need to be updated every year. " />
                </Typography>
                <Box marginTop={theme.spacing(theme.tokens.spacing.sm)}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{
                      backgroundColor: "white"
                    }}
                    onClick={() => {
                      history.push(Locations.Home.EnrollmentFlowActive)
                    }}
                  >
                    <FormattedMessage defaultMessage="Share insurance info" />
                  </Button>
                </Box>
              </Alert>
            </Box>
          )}

          {partnerEligibilityStatus === PartnerEligibilityStatus.POTENTIALLY_ELIGIBLE_MISSING_PARTNER_INFO && (
            <Box marginBottom={theme.spacing(theme.tokens.spacing.lg)}>
              <Alert severity="warning">
                <Typography>
                  <FormattedMessage defaultMessage="Answer a few questions about your partner to see if they're eligible for coverage and savings at partner providers. This information needs to be updated every year." />
                </Typography>
                <Box marginTop={theme.spacing(theme.tokens.spacing.sm)}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{
                      backgroundColor: "white"
                    }}
                    onClick={() => {
                      window.location.href = accountSettingsUrl
                    }}
                  >
                    <FormattedMessage defaultMessage="Update partner info"></FormattedMessage>
                  </Button>
                </Box>
              </Alert>
            </Box>
          )}

          <Typography variant="h2" marginBottom={theme.spacing(theme.tokens.spacing.xs)}>
            <FormattedMessage defaultMessage="Get faster service" />
          </Typography>
          <Typography variant="body1" marginBottom={theme.spacing(theme.tokens.spacing.xxxl)}>
            <FormattedMessage
              defaultMessage="When you <link>call us</link>, you can use your member ID to identify yourself and get support quickly and easily."
              values={{
                link: (linkContent) => (
                  <Link target="_blank" href={TtcPaths.TTC_CARE_NAV}>
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
          <PartnerProvidersSection hasProviderFinder={hasProviderFinder} includeFallbackCopy />

          <MemberIdCard
            firstName={firstName}
            lastName={lastName}
            isUsa={isUsa}
            memberId={memberId}
            effectiveDate={effectiveDate}
            partnerFirstName={partnerFirstName}
            partnerLastName={partnerLastName}
            isLoading={isLoading}
            isError={isError}
            partnerShouldAppearOnMemberCard={partnerShouldAppearOnMemberCard}
            pharmacyInfo={pharmacyInfo}
          />
          <Stack marginTop={theme.spacing(theme.tokens.spacing.lg)}>
            {partnerShouldAppearOnMemberCard && (
              <Link href={accountSettingsUrl}>
                <Typography>
                  <FormattedMessage defaultMessage="Update partner information" />
                </Typography>
              </Link>
            )}

            {partnerNotEligible && (
              <Typography variant="body1" marginBottom={theme.spacing(theme.tokens.spacing.lg)}>
                <FormattedMessage
                  defaultMessage="Your partner is not <link1>eligible</link1> for coverage and savings at partner providers at this time."
                  values={{
                    link1: (linkContent) => (
                      <Link target="_blank" href={DbgPaths.CARROT_ELIGIBLE}>
                        {linkContent}
                      </Link>
                    )
                  }}
                />
              </Typography>
            )}
          </Stack>
        </>
      )}
    </PageLayout>
  )
}
